<template>
  <div class="bim-category-params-page">
    <div class="page-header">
      <PageTitle hideMarginLeft>Параметры категорий БИМ</PageTitle>
    </div>

    <div class="layout-with-drawer tw-pt-4">
      <!-- Левая панель: категории (как в настройке пакетов) -->
      <div class="drawer-container">
        <div class="drawer-sidebar">
          <q-list class="drawer-list">
            <q-item
              v-for="category in categories"
              :key="category.bim_category_id"
              clickable
              v-ripple
              :active="selectedCategoryId === category.bim_category_id"
              @click="selectCategory(category)"
              active-class="text-white tw-bg-primary-light"
              class="drawer-item"
            >
              <q-item-section>
                <q-item-label>{{ category.category_name }}</q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
          <div v-if="!isLoadingCategories && categories.length === 0" class="drawer-empty">
            <q-icon name="info" size="32px" color="grey-5" />
            <p>Нет категорий</p>
          </div>
          <div v-if="isLoadingCategories" class="drawer-loading">
            <q-spinner color="primary" size="24px" />
          </div>
        </div>
      </div>

      <!-- Правая часть: табы Элементы / Параметры + контент -->
      <div class="content-container">
        <div class="content-body">
          <CustomTabs :tab="tab" :data="tabsData" @change_tab="onTabChange" alt paddingless>
            <template v-slot:default="{ data }">
              <component
                :is="components[data.component]"
                :key="data.name"
                :selected-category="selectedCategory"
                :selected-category-id="selectedCategoryId"
                @import-done="refetchCategories"
              />
            </template>
          </CustomTabs>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import PageTitle from '@/components/common/PageTitle.vue'
import CustomTabs from '@/components/common/CustomTabs'
import { ref, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ElementsTab from './ElementsTab.vue'
import ParamsTab from './ParamsTab.vue'
import { getBimCategories } from '@/views/BimCategoryParams/services'

const route = useRoute()
const router = useRouter()

const components = { ElementsTab, ParamsTab }
const tabsData = [
  { name: 'elements', label: 'Элементы', component: 'ElementsTab' },
  { name: 'params', label: 'Параметры', component: 'ParamsTab' }
]

const tab = ref(route.query.tab || 'elements')
const categories = ref([])
const selectedCategory = ref(null)
const selectedCategoryId = ref(null)
const isLoadingCategories = ref(false)

watch(
  () => route.query.tab,
  (queryTab) => {
    if (queryTab === 'elements' || queryTab === 'params') {
      tab.value = queryTab
    }
  },
  { immediate: true }
)

onMounted(async () => {
  isLoadingCategories.value = true
  try {
    const res = await getBimCategories()
    if (res) {
      categories.value = res
      if (categories.value.length > 0 && !selectedCategoryId.value) {
        selectCategory(categories.value[0])
      }
    }
  } catch (e) {
    console.error(e)
  } finally {
    isLoadingCategories.value = false
  }
})

function selectCategory(category) {
  selectedCategory.value = category
  selectedCategoryId.value = category.bim_category_id
}

async function refetchCategories() {
  try {
    const res = await getBimCategories()
    if (res) {
      categories.value = res
      if (selectedCategoryId.value) {
        const found = res.find(c => c.bim_category_id === selectedCategoryId.value)
        if (found) selectedCategory.value = found
      }
    }
  } catch (e) {
    console.error(e)
  }
}

function onTabChange(value) {
  tab.value = value
  router.push({
    name: 'bimCategoryParams',
    query: { tab: value }
  }).catch(() => {})
}
</script>

<style scoped lang="scss">
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;

  :deep(.title) {
    margin-bottom: 0;
    flex: 1;
  }
}

.layout-with-drawer {
  display: flex;
  gap: 0;
  height: 100%;
  min-height: 500px;
}

.drawer-container {
  flex-shrink: 0;
  width: 280px;
}

.drawer-sidebar {
  height: 100%;
  border-radius: 8px 0 0 8px;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.drawer-list {
  padding: 16px 8px;
  flex: 1;
  overflow-y: auto;
}

.drawer-item {
  border-radius: 8px;
  margin-bottom: 4px;
}

.drawer-empty,
.drawer-loading {
  padding: 24px 16px;
  text-align: center;
  color: #999;
}

.drawer-empty p,
.drawer-loading p {
  margin-top: 8px;
  font-size: 14px;
}

.content-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-radius: 0 8px 8px 0;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.content-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px;
}

@media (max-width: 768px) {
  .layout-with-drawer {
    flex-direction: column;
  }

  .drawer-container {
    width: 100%;
    max-height: 200px;
  }
}
</style>
